import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { mergeMap, takeUntil } from "rxjs/operators";
import { SubscriptionDTO } from "src/app/data/schema/Subscriptions/SubscriptionDTO";
import { TimeLimitationConfigDTO } from "src/app/data/schema/Subscriptions/TimeLimitationConfigDTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import enUS from "../i18n/en-US.json";
import esES from "../i18n/es-ES.json";
import ptBR from "../i18n/pt-BR.json";
import { CommunicationService } from "./Communication/communication.service";
import { AuthService } from "./auth.service";
import { SnackbarService } from "./snackbar.service";
import { SubscriptionService } from "./subscription.service";

@Injectable({
    providedIn: "root"
})
export class UserService {
    private subscription: SubscriptionDTO = null;
    private user$: BehaviorSubject<UsuarioTO>;
    private _unsubscribeAll: Subject<any> = new Subject();
    private loopTimeValidation: NodeJS.Timeout;

    constructor(
        private userService: ApiUserService,
        private translate: TranslateService,
        private snackBar: SnackbarService,
        private authService: AuthService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private communicationService: CommunicationService,
        private coursesService: ApiCoursesService
    ) {
        this.translate.setTranslation("pt-BR", ptBR, true);
        this.translate.setTranslation("en-US", enUS, true);
        this.translate.setTranslation("es-ES", esES, true);

        this.user$ = new BehaviorSubject<UsuarioTO>(null);

        this.authService.sessionAsync.subscribe((session) => {
            if (!session) this.user$.next(null);
        });

        this.subscriptionService
            .currentSubscriptionAsync()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((sub) => {
                this.subscription = sub;
            });
    }

    getUserById(userId: string): void {
        this.userService.getUserById(userId).subscribe((user) => {
            this.user$.next(user);
            localStorage["usr"] = JSON.stringify(user);

            if (user.preferencias?.idioma) this.translate.use(user.preferencias?.idioma);
            else this.subscriptionService.setAppLanguage();

            this.verifUserPendingIssues();
            this.communicationService.startVerify();
            this.verifyUserCanbeLogged();
        });
    }

    public getCurrentUser = (): Observable<UsuarioTO> => this.user$.asObservable();
    public setCurrentUser = (user: UsuarioTO): void => {
        this.user$.next(user);
        localStorage["usr"] = JSON.stringify(user);
    };

    public verifUserPendingIssues(): void {
        if (this.user$?.value == null || this.subscription == null) return;
        console.log("checking user pending issues");

        if (this.user$.value.requestPasswordChange) {
            const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
            const requestPasswordChange = new Date(this.user$.value.requestPasswordChange);
            const dayChange = new Date(
                requestPasswordChange.getFullYear(),
                requestPasswordChange.getMonth(),
                requestPasswordChange.getDate()
            );
            if (
                dayChange <= today &&
                (!this.user$.value.passwordChangedAt ||
                    this.user$.value.passwordChangedAt < this.user$.value.requestPasswordChange)
            ) {
                console.log(`user need change password`);

                this.router.navigate(["/change-password"]);
                return;
            }
        }

        if (
            !this.user$.value.dataReviewedAt ||
            this.subscription.dataReviewRequestedAt > this.user$.value.dataReviewedAt
        ) {
            console.log(
                `complete-registration user ${this.user$.value.dataReviewedAt}  sub ${this.subscription.dataReviewRequestedAt}`
            );

            // Verificar se o usuário escolheu pular o cadastro completo anteriormente
            const userSkippedRegistration = localStorage.getItem('skipCompleteRegistration') === 'true';
            if (userSkippedRegistration) {
                console.log('Usuário pulou o cadastro completo anteriormente - não redirecionando');
                return;
            }

            // Verificar se está navegando para rota de curso e se o curso permite pular cadastro completo
            const currentUrl = this.router.url;
            const courseRouteMatch = currentUrl.match(/\/cursos\/([a-f0-9-]+)/);

            if (courseRouteMatch) {
                const courseId = courseRouteMatch[1];
                console.log(`Verificando skipCompleteRegistration para curso: ${courseId}`);

                // Verificar se o curso permite pular o cadastro completo
                this.coursesService.getCourseApresentation(courseId).subscribe(
                    (course) => {
                        if (course?.skipCompleteRegistration) {
                            console.log(`Curso ${courseId} permite pular cadastro completo - não redirecionando`);
                            return;
                        } else {
                            console.log(`Curso ${courseId} não permite pular cadastro completo - redirecionando`);
                            this.redirectToCompleteRegistration();
                        }
                    },
                    (error) => {
                        console.log(
                            `Erro ao verificar curso ${courseId}, redirecionando para cadastro completo:`,
                            error
                        );
                        this.redirectToCompleteRegistration();
                    }
                );
            } else {
                // Se não é rota de curso, redirecionar normalmente
                this.redirectToCompleteRegistration();
            }
        }
    }

    private redirectToCompleteRegistration(): void {
        this.router.navigate(["complete-registration", this.user$.value.id], {
            queryParams: { callbackUrl: this.router.url }
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        if (this.loopTimeValidation) clearInterval(this.loopTimeValidation);
    }

    updateUserPassword(oldPassword: string, newPassword: string): Observable<any> {
        return new Observable<boolean>((o) =>
            this.authService
                .changePasswordLogged(oldPassword, newPassword)
                .pipe(
                    mergeMap(() => {
                        return this.userService.passwordChanged(this.authService.getClaimsUserId());
                    }),
                    mergeMap(() => {
                        return this.userService.getUserById(this.authService.getClaimsUserId());
                    })
                )
                .subscribe(
                    (user) => {
                        this.setCurrentUser(user);
                        o.next(true);
                    },
                    (err) => o.error(err),
                    () => o.complete()
                )
        );
    }

    verifyUserCanbeLogged(): void {
        if (this.loopTimeValidation) clearInterval(this.loopTimeValidation);
        if (this.user$?.value == null || this.subscription == null) return;

        if (this.authService.hasAnyRole(["Admin", "SuperAdmin", "Guru"])) {
            return;
        }

        const timeLimitationConfig =
            this.subscription.loginSettings?.accessLimitationConfig?.timeLimitationConfig.filter(
                (t) => t.active == true
            );

        if (!timeLimitationConfig || timeLimitationConfig?.length == 0) return;

        this.loopTimeValidation = setInterval(() => {
            const valid = this.validateTimeLimitation(timeLimitationConfig);
            if (!valid) {
                this.authService.logout().subscribe(() => {
                    this.snackBar.showMessage(
                        this.translate.instant("SERVICES_MESSEGES.USER_SERVICE.TIME_LIMITATION_INVALID"),
                        10000,
                        true
                    );
                    this.router.navigate(["/"]);
                });
                console.log("Usuário deslogado por esta fora do horario permitido");
                clearInterval(this.loopTimeValidation);
            }
        }, 60000);
    }

    validateTimeLimitation(timeLimitationConfig: TimeLimitationConfigDTO[]): boolean {
        const user = this.user$?.value;

        if (user == null || this.subscription == null) return;

        const now = new Date();
        const utcHours = now.getUTCHours();
        const utcMinutes = now.getUTCMinutes();
        const currentTime = utcHours * 60 + utcMinutes; // Converter hora atual para minutos totais para comparação
        const currentDayOfWeek = now.getUTCDay();

        return timeLimitationConfig.some((config) => {
            const [startHours, startMinutes] = config.startTime.split(":").map(Number);
            const [endHours, endMinutes] = config.endTime.split(":").map(Number);

            const startTimeInMinutes = startHours * 60 + startMinutes;
            const endTimeInMinutes = endHours * 60 + endMinutes;

            // Verifica se a hora atual está no intervalo
            let isInRange = false;

            if (startTimeInMinutes <= endTimeInMinutes) {
                // Intervalo não cruza a meia-noite
                isInRange = currentTime >= startTimeInMinutes && currentTime <= endTimeInMinutes;
            } else {
                // Intervalo cruza a meia-noite
                isInRange = currentTime >= startTimeInMinutes || currentTime <= endTimeInMinutes;
            }

            // Verifica se o dia atual está na lista de dias permitidos
            const isCorrectDay =
                config.dayOfWeek && config.dayOfWeek.length > 0 && config.dayOfWeek.includes(currentDayOfWeek);

            return isInRange && isCorrectDay;
        });
    }
}
